import { svgPathData as faCirclePath } from "@fortawesome/pro-regular-svg-icons/faCircle";
import { svgPathData as faMapMarkerPinPath } from "@fortawesome/pro-solid-svg-icons/faMapMarkerAlt";

import MarkerIcon from "@mapmycustomers/shared/enum/MarkerIcon";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import {
  Activity,
  AnyEntity,
  Company,
  Deal,
  EntityType,
  Person,
} from "@mapmycustomers/shared/types/entity";

import colors from "@app/styles/_colors.module.scss";
import anyColorToHex from "@app/util/colors/anyColorToHex";

type MarkerOptions = google.maps.MarkerOptions;

export type MarkerOptionsGetter<T extends AnyEntity = AnyEntity> = (
  entity: T,
  entityType: EntityType,
  markerSize: MarkerSize,
  markerIcon: MarkerIcon
) => MarkerOptions;

const defaultMarkerOptionsGetter: MarkerOptionsGetter = (
  entity,
  entityType,
  markerSize,
  markerIcon
) => {
  // size of the marker, careful! this scale also affects anchor and labelOrigin
  const scale = markerSize === MarkerSize.LARGE ? 0.09 : 0.05;
  const anchor = new window.google.maps.Point(200, 510);
  const labelOrigin = new window.google.maps.Point(200, 205);
  const path = markerIcon === MarkerIcon.PIN ? faMapMarkerPinPath : faCirclePath;
  const defaultIconProps = (color: string): MarkerOptions["icon"] => ({
    anchor,
    fillColor: markerIcon === MarkerIcon.CIRCLE ? undefined : color,
    fillOpacity: 1,
    labelOrigin,
    path,
    scale,
    strokeColor: markerIcon === MarkerIcon.CIRCLE ? color : colors.white,
    strokeWeight: markerIcon === MarkerIcon.CIRCLE ? 4 : 2,
  });
  const getColor = (entity: Company | Person): string => {
    let pinColor = colors.slate;

    if (entity.outOfCadence) {
      pinColor = colors.red100;
    } else if (entity.outOfCadence === false) {
      // white color only if false, not null
      pinColor = colors.white;
    } else if (entity.color) {
      pinColor = anyColorToHex(entity.color);
    }

    return pinColor;
  };

  switch (entityType) {
    case EntityType.ACTIVITY: {
      const activity = entity as Activity;
      const associatedRecord = activity.account ?? activity.contact;
      return {
        icon: defaultIconProps(
          associatedRecord?.color ? anyColorToHex(associatedRecord.color) : colors.slate
        ),
      };
    }
    case EntityType.COMPANY:
      return {
        icon: defaultIconProps(getColor(entity as Company)),
      };
    case EntityType.DEAL: {
      const deal = entity as Deal;
      return {
        icon: defaultIconProps(deal.stage.color ? anyColorToHex(deal.stage.color) : colors.slate),
      };
    }
    case EntityType.PERSON:
      return {
        icon: defaultIconProps(getColor(entity as Person)),
      };
    default:
      return {};
  }
};

export default defaultMarkerOptionsGetter;
